'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle } from 'lucide-react';
import { cn } from './lib/utils';
import { elevationVariants, type ElevationProps } from './effects/elevation';

type AlertVariant = 'contained' | 'outlined';

const alertVariants = cva(
  'relative w-full rounded-lg px-4 py-3.5 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        contained: 'border-0 bg-background-primary-alert text-primary',
        outlined:
          'border border-gray-300 text-gray-400 border-input bg-transparent',
      },
      severity: {
        primary: 'bg-primary-alert text-primary',
        secondary: 'bg-secondary-alert text-secondary',
        success: 'bg-success-alert text-success',
        error: 'bg-error-alert text-error',
        info: 'bg-info-alert text-info',
        warning: 'bg-warning-alert text-warning',
      },
    },
    compoundVariants: [
      ...(
        ['primary', 'secondary', 'success', 'error', 'info', 'warning'] as const
      ).flatMap((severity) => [
        {
          variant: 'outlined' as AlertVariant,
          severity,
          className: `border-${severity} text-${severity} bg-transparent`,
        },
      ]),
    ],
    defaultVariants: {
      variant: 'contained',
      severity: 'primary',
    },
  }
);

// Create Alert Context
const AlertContext = React.createContext<VariantProps<typeof alertVariants>>(
  {}
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof alertVariants> &
    ElevationProps
>(
  (
    {
      className,
      variant = 'contained',
      severity,
      elevation,
      children,
      ...props
    },
    ref
  ) => {
    const elevationClass =
      variant === 'contained' || !variant
        ? elevationVariants({ elevation })
        : '';

    return (
      <AlertContext.Provider value={{ variant, severity }}>
        <div
          ref={ref}
          role="alert"
          className={cn(
            alertVariants({ variant, severity }),
            elevationClass,
            className
          )}
          {...props}
        >
          {children}
        </div>
      </AlertContext.Provider>
    );
  }
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & { showIcon?: boolean }
>(({ className, showIcon, ...props }, ref) => {
  const { severity } = React.useContext(AlertContext);
  return (
    <div className="flex items-center gap-2">
      {showIcon && (
        <AlertCircle className={cn('mb-1 h-4 w-4', `text-${severity}`)} />
      )}
      <h5
        ref={ref}
        className={cn(
          'mb-1 font-medium leading-none tracking-tight',
          severity && `text-${severity}`,
          className
        )}
        {...props}
      />
    </div>
  );
});
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & { showIcon?: boolean }
>(({ className, showIcon, ...props }, ref) => {
  const { severity } = React.useContext(AlertContext);
  return (
    <div className="flex items-start gap-2">
      {showIcon && (
        <AlertCircle className={cn('h-4 w-4 mt-0.5', `text-${severity}`)} />
      )}
      <div
        ref={ref}
        className={cn(
          'text-sm [&_p]:leading-relaxed',
          severity && `text-${severity}`,
          className
        )}
        {...props}
      />
    </div>
  );
});
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
